import { useCallback, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import { ConfirmContext, type ConfirmOptions } from '../contexts/confirmContext'

/** Diálogo de confirmação partilhado, servido pelo hook `useConfirm()`. */
export default function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common')
  const [options, setOptions] = useState<ConfirmOptions | null>(null)
  const resolver = useRef<((value: boolean) => void) | null>(null)

  const confirm = useCallback((next: ConfirmOptions) => {
    setOptions(next)
    return new Promise<boolean>((resolve) => { resolver.current = resolve })
  }, [])

  const close = (value: boolean) => {
    resolver.current?.(value)
    resolver.current = null
    setOptions(null)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={!!options} onClose={() => close(false)} maxWidth="xs" fullWidth={false} aria-labelledby="confirm-dialog-title">
        <DialogTitle id="confirm-dialog-title" sx={{ fontWeight: 800 }}>
          {options?.title ?? t('confirm.title')}
        </DialogTitle>
        <DialogContent>
          {options?.message && <Typography variant="body2">{options.message}</Typography>}
          {options?.destructive && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: options.message ? 1 : 0 }}>
              {t('confirm.destructiveHint')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button variant="outlined" onClick={() => close(false)}>
            {options?.cancelLabel ?? t('actions.cancel')}
          </Button>
          <Button
            variant="contained"
            color={options?.destructive ? 'error' : 'primary'}
            onClick={() => close(true)}
            autoFocus
          >
            {options?.confirmLabel ?? t('actions.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  )
}
